/**
 * AirRiskMitigations Component
 *
 * Displays the ten air risk strategic mitigations for SORA evidence review.
 * Uses the MitigationCard component to display each mitigation with
 * yes/no value, commentary, evidence, and citations.
 *
 * Air Risk Strategic Mitigations:
 * - SM1: Operational restriction by boundary (yes/no)
 * - SM2: Operational restriction by chronology (yes/no)
 * - SM3: Operational restriction by time of exposure (yes/no)
 * - SM4: Special Use Airspace (SUA) (yes/no)
 * - SM5: Other airspace requirements (yes/no)
 * - SM6: Pre-agreement of ANSP services (yes/no)
 * - SM7: Pre-agreement of UTM services (yes/no)
 * - SM8: NOTAM of intended operation (yes/no)
 * - SM9: Military low flying notification (yes/no)
 * - SM10: Outreach to local flying clubs and pilots (yes/no)
 *
 * Requirements: 7.1, 7.2, 7.3
 */

import React, { useMemo } from 'react';
import {
  MitigationCard,
  fromAirRiskMitigation,
} from '../MitigationCard';
import {
  AirRiskMitigation,
  AirRiskMitigationCode,
  Citation,
} from '../../types/extraction';
import { Feedback } from '../../types/feedback';
import styles from './AirRiskMitigations.module.css';

/**
 * Human-readable names for air risk strategic mitigation codes
 */
const MITIGATION_NAMES: Record<AirRiskMitigationCode, string> = {
  SM1: 'Operational restriction by boundary',
  SM2: 'Operational restriction by chronology',
  SM3: 'Operational restriction by time of exposure',
  SM4: 'Special Use Airspace (SUA)',
  SM5: 'Other airspace requirements',
  SM6: 'Pre-agreement of ANSP services',
  SM7: 'Pre-agreement of UTM services',
  SM8: 'NOTAM of intended operation',
  SM9: 'Military low flying notification',
  SM10: 'Outreach to local flying clubs and pilots',
};

/**
 * Descriptions for each strategic mitigation type
 */
const MITIGATION_DESCRIPTIONS: Record<AirRiskMitigationCode, string> = {
  SM1: 'Restricting operations to specific geographic boundaries to reduce air risk',
  SM2: 'Restricting operations to specific times/dates to avoid air traffic',
  SM3: 'Limiting the duration of exposure in airspace to reduce collision risk',
  SM4: 'Operating within Special Use Airspace with controlled access',
  SM5: 'Compliance with additional airspace requirements beyond standard rules',
  SM6: 'Pre-coordination with Air Navigation Service Provider for airspace access',
  SM7: 'Pre-coordination with UTM service provider for traffic management',
  SM8: 'Publishing Notice to Airmen to alert other airspace users',
  SM9: 'Notifying military authorities of operations in low flying areas',
  SM10: 'Engaging with local aviation community to raise awareness of operations',
};

/**
 * Order of mitigations for display (SM1 through SM10)
 */
const MITIGATION_ORDER: AirRiskMitigationCode[] = [
  'SM1',
  'SM2',
  'SM3',
  'SM4',
  'SM5',
  'SM6',
  'SM7',
  'SM8',
  'SM9',
  'SM10',
];

/**
 * Props for the AirRiskMitigations component
 */
export interface AirRiskMitigationsProps {
  /** Array of air risk mitigations to display */
  mitigations: AirRiskMitigation[];
  /** Callback when a citation is clicked */
  onCitationClick: (citation: Citation) => void;
  /** Callback when the feedback button is clicked for a mitigation */
  onFeedbackClick: (mitigationId: string) => void;
  /** Optional map of feedback records by mitigation ID */
  feedbackMap?: Map<string, Feedback>;
  /** Optional loading state */
  isLoading?: boolean;
  /** Optional title for the section */
  title?: string;
  /** Optional description for the section */
  description?: string;
}

/**
 * Sorts mitigations according to the defined order (SM1 through SM10)
 */
function sortMitigationsByOrder(
  mitigations: AirRiskMitigation[]
): AirRiskMitigation[] {
  return [...mitigations].sort((a, b) => {
    const indexA = MITIGATION_ORDER.indexOf(a.code);
    const indexB = MITIGATION_ORDER.indexOf(b.code);
    return indexA - indexB;
  });
}

/**
 * AirRiskMitigations displays the ten air risk strategic mitigations in a grid layout.
 * It uses the MitigationCard component to provide consistent styling and functionality
 * including yes/no status badges, LLM commentary, evidence, clickable citations, and feedback buttons.
 *
 * @example
 * <AirRiskMitigations
 *   mitigations={extractionResults.airRiskMitigations}
 *   onCitationClick={handleCitationClick}
 *   onFeedbackClick={handleFeedbackClick}
 * />
 */
export const AirRiskMitigations: React.FC<AirRiskMitigationsProps> = ({
  mitigations,
  onCitationClick,
  onFeedbackClick,
  feedbackMap,
  isLoading = false,
  title = 'Air Risk Strategic Mitigations',
  description = 'Review the extracted air risk strategic mitigations for your SORA application. Each mitigation includes LLM-generated commentary and supporting evidence.',
}) => {
  // Sort mitigations by defined order and prepare for rendering
  const sortedMitigations = useMemo(() => {
    return sortMitigationsByOrder(mitigations);
  }, [mitigations]);

  // Count how many of the expected mitigations are present
  const presentMitigationCodes = useMemo(() => {
    return new Set(mitigations.map((m) => m.code));
  }, [mitigations]);

  // Categorise mitigations by yes/no for the summary pills
  const statusCounts = useMemo(() => {
    let yes = 0;
    let no = 0;

    for (const m of mitigations) {
      if (m.selectedValue) {
        yes++;
      } else {
        no++;
      }
    }

    return { total: mitigations.length, yes, no };
  }, [mitigations]);

  const mitigationCount = presentMitigationCodes.size;
  const totalExpected = MITIGATION_ORDER.length;

  if (isLoading) {
    return (
      <section
        className={styles.container}
        aria-labelledby="air-risk-mitigations-title"
        aria-busy="true"
      >
        <header className={styles.header}>
          <h2 id="air-risk-mitigations-title" className={styles.title}>
            {title}
          </h2>
          {description && <p className={styles.description}>{description}</p>}
        </header>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} aria-label="Loading mitigations" />
          <p className={styles.loadingText}>Loading air risk mitigations...</p>
        </div>
      </section>
    );
  }

  if (mitigations.length === 0) {
    return (
      <section
        className={styles.container}
        aria-labelledby="air-risk-mitigations-title"
      >
        <header className={styles.header}>
          <h2 id="air-risk-mitigations-title" className={styles.title}>
            {title}
          </h2>
          {description && <p className={styles.description}>{description}</p>}
        </header>
        <div className={styles.emptyState}>
          <p className={styles.emptyMessage}>
            No air risk mitigations available. Please process your documents to
            extract evidence.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      className={styles.container}
      aria-labelledby="air-risk-mitigations-title"
    >
      <header className={styles.header}>
        <h2 id="air-risk-mitigations-title" className={styles.title}>
          {title}
        </h2>
        {description && <p className={styles.description}>{description}</p>}
      </header>

      {/* Mitigation Summary Pills */}
      <div className={styles.summaryBar} role="status" aria-label="Mitigation summary">
        <span className={styles.summaryPill}>
          <span className={styles.summaryCount}>{statusCounts.total}</span> mitigations
        </span>
        <span className={styles.summaryPill}>
          <span className={styles.summaryDot} style={{ background: '#a0a0a0' }} aria-hidden="true" />
          <span className={styles.summaryCount}>{statusCounts.no}</span> No
        </span>
        <span className={styles.summaryPill}>
          <span className={styles.summaryDot} style={{ background: '#107c10' }} aria-hidden="true" />
          <span className={styles.summaryCount}>{statusCounts.yes}</span> Yes
        </span>
      </div>

      {/* Mitigations Grid */}
      <div className={styles.mitigationsGrid}>
        {sortedMitigations.map((mitigation) => {
          const feedback = feedbackMap?.get(mitigation.id);
          const mitigationData = fromAirRiskMitigation(mitigation, feedback);

          return (
            <MitigationCard
              key={mitigation.id}
              mitigation={mitigationData}
              onCitationClick={onCitationClick}
              onFeedbackClick={onFeedbackClick}
            />
          );
        })}
      </div>

      {/* Footer with count */}
      <footer className={styles.footer}>
        <p className={styles.footerText}>
          <span className={styles.mitigationCount}>{mitigationCount}</span> of{' '}
          <span className={styles.totalMitigations}>{totalExpected}</span> air
          risk strategic mitigations extracted
        </p>
        {mitigationCount < totalExpected && (
          <p className={styles.missingWarning}>
            Missing mitigations:{' '}
            {MITIGATION_ORDER.filter((code) => !presentMitigationCodes.has(code)).join(
              ', '
            )}
          </p>
        )}
      </footer>
    </section>
  );
};

export default AirRiskMitigations;
